import { useMutation } from '@apollo/client';
import { TextInputLight } from 'components/site/layouts/DefaultPageLayout/TextInputLight';

import { S_WebButtonSmaller } from 'elements/button/WebButton';
import {
    ResetUserPassword,
    ResetUserPasswordVariables
} from 'libs/graphql/mutation/access/__generated__/ResetUserPassword';
import { RESET_USER_PASSWORD_MUTATION } from 'libs/graphql/mutation/access/accessMutation';
import { EShadows } from 'libs/styles/styling';
import { EToastType } from 'libs/types/site/IClient';
import { userMessage } from 'libs/utils/logic/ui/uiLogic';
import { useContext, useState } from 'react';
import { useNavigate } from 'react-router';
import { GlobalContext } from 'session/state/context';
import { setLoggedUser } from 'session/state/user/userActions';
import { Localization } from 'static/localization/localization';
import styled from 'styled-components/macro';

interface Props {
    className?: string;
    email: string;
    hash: string;
}

export const NewPasswordForm = (props: Props): JSX.Element => {
    const [password, setPassword] = useState('');
    const navigate = useNavigate();
    const [confirmPassword, setConfirmPassword] = useState('');
    const { dispatch } = useContext(GlobalContext);

    const [sendRequest] = useMutation<ResetUserPassword, ResetUserPasswordVariables>(
        RESET_USER_PASSWORD_MUTATION,
        {
            variables: {
                password,
                confirmPassword,
                email: props.email,
                hash: props.hash
            },
            onCompleted: (result) => {
                dispatch(
                    setLoggedUser(
                        result.resetUserPassword.user,
                        result.resetUserPassword.tokenType,
                        result.resetUserPassword.accessToken
                    )
                );
                userMessage(EToastType.SUCCESS, Localization.WELCOME);
                navigate('/admin');
            },
            onError: (err) => {
                const r = err.message;
                if (r === 'RESET_CODE_EXPIRED' || r === 'RESET_CODE_USED') {
                    userMessage(EToastType.ERROR, Localization.ERROR_RESET_CODE);
                } else if (r === 'INVALID_PASSWORD') {
                    userMessage(EToastType.ERROR, Localization.PASSWORD_NOT_STRONG);
                } else {
                    userMessage(EToastType.ERROR, Localization.ERROR_RESETTING);
                }
            }
        }
    );

    const canSubmit = password && confirmPassword;

    return (
        <S_NewPasswordForm className={props.className}>
            <form
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        e.preventDefault();
                        if (canSubmit) {
                            sendRequest();
                        }
                    }
                }}
            >
                <input
                    style={{ display: 'none' }}
                    type="email"
                    autoComplete="username"
                    value={props.email}
                />
                <S_TextInputLight
                    value={password}
                    label={Localization.NEW_PASSWORD}
                    autoComplete="new-password"
                    autoFocus={true}
                    onChange={(value) => {
                        setPassword(value);
                    }}
                    type="password"
                />
                <S_TextInputLight
                    value={confirmPassword}
                    label={Localization.CONFIRM_PASSWORD}
                    autoComplete="new-password"
                    onChange={(value) => {
                        setConfirmPassword(value);
                    }}
                    type="password"
                />
                <S_Center>
                    <S_WebButton
                        text={Localization.SET_NEW_PASSWORD}
                        type="primary"
                        shadow={EShadows.LIGHT}
                        onClick={() => {
                            sendRequest();
                        }}
                        disabled={!canSubmit}
                    />
                </S_Center>
            </form>
        </S_NewPasswordForm>
    );
};

const S_NewPasswordForm = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
`;

const S_TextInputLight = styled(TextInputLight)`
    margin-bottom: 20px;
`;

const S_WebButton = styled(S_WebButtonSmaller)`
    margin: 0 0 25px 0;
`;

const S_Center = styled.div`
    display: flex;
    justify-content: center;
`;
